/**
 * Help primitives — Section / Example / Kbd building blocks used inside the
 * `HowItWorksPanel` body. Keep visual style consistent across tabs.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface HelpSectionProps {
  title: string;
  children: ReactNode;
}

const HelpSection: FC<HelpSectionProps> = ({ title, children }) => (
  <section className="space-y-1">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
      {title}
    </h4>
    <div className="space-y-1 text-xs text-muted-foreground">{children}</div>
  </section>
);

export interface HelpExampleProps {
  pattern: string;
  effect: string;
  className?: string;
}

const HelpExample: FC<HelpExampleProps> = ({ pattern, effect, className }) => (
  <div
    className={cn(
      'flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs',
      className,
    )}
  >
    <code className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground">
      {pattern}
    </code>
    <span className="text-muted-foreground">→ {effect}</span>
  </div>
);

const HelpKbd: FC<{ children: ReactNode }> = ({ children }) => (
  <kbd className="rounded border border-border bg-background px-1 py-0.5 font-mono text-[10.5px] text-foreground">
    {children}
  </kbd>
);

const HelpInline: FC<{ children: ReactNode }> = ({ children }) => (
  <strong className="font-semibold text-foreground">{children}</strong>
);

export const Help = {
  Section: HelpSection,
  Example: HelpExample,
  Kbd: HelpKbd,
  Inline: HelpInline,
};
